// ─────────────────────────────────────────────────────────────
// DisciplineAnalytics — full-width two-column layout
// ─────────────────────────────────────────────────────────────

import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { tradeAmount } from "../../lib/format";
import { classifyTrade } from "../../lib/analytics";
import { useViewport } from "../../hooks/useViewport";

export function DisciplineAnalytics({ trades, setView, accounts = [], currentAccountId, setCurrentAccountId, pnlMode = "net" }) {
  const { isMobile } = useViewport();
  const accountTrades = (currentAccountId
    ? trades.filter(t => t.account_id === currentAccountId)
    : trades
  ).sort((a, b) => new Date(b.created_at || b.trade_date) - new Date(a.created_at || a.trade_date));

  const hasEnough = accountTrades.length >= 3;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const avg = (arr, field) => {
    const vals = arr.map(x => x[field]).filter(v => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const wr = arr => {
    let wins = 0, losses = 0;
    for (const t of arr) {
      const k = classifyTrade(t);
      if (k === "win") wins++;
      else if (k === "loss") losses++;
    }
    const denom = wins + losses;
    return denom > 0 ? wins / denom : null;
  };
  const pct = v => v != null ? `${(v * 100).toFixed(0)}%` : "—";
  const avgFmt = (arr, field) => { const v = avg(arr, field); return v != null ? v.toFixed(1) : "—"; };

  // ── Discipline trend (last 10) ────────────────────────────────────────────
  const last10 = accountTrades.slice(0, 10).map(t => t.discipline_score).filter(Boolean);
  const disciplineTrend = last10.length >= 3
    ? last10[0] > last10[last10.length - 1] ? "improving"
      : last10[0] < last10[last10.length - 1] ? "declining" : "stable"
    : null;
  const avgDiscipline = avg(accountTrades, "discipline_score");

  // ── Win rate by mental state ───────────────────────────────────────────────
  const stateMap = {};
  accountTrades.forEach(t => {
    const s = t.mental_state || "Unknown";
    if (!stateMap[s]) stateMap[s] = { wins: 0, losses: 0, total: 0 };
    stateMap[s].total++;
    const k = classifyTrade(t);
    if (k === "win") stateMap[s].wins++;
    else if (k === "loss") stateMap[s].losses++;
  });
  const stateRows = Object.entries(stateMap)
    .filter(([, d]) => d.total >= 2)
    .map(([state, d]) => {
      const denom = d.wins + d.losses;
      return { state, wr: denom > 0 ? d.wins / denom : null, total: d.total };
    })
    .filter(r => r.wr !== null)
    .sort((a, b) => b.wr - a.wr);

  // ── Fear/Greed vs calm ────────────────────────────────────────────────────
  const calm      = accountTrades.filter(t => (t.fear_level || 0) <= 4 && (t.greed_level || 0) <= 4);
  const highFear  = accountTrades.filter(t => (t.fear_level  || 0) >= 7);
  const highGreed = accountTrades.filter(t => (t.greed_level || 0) >= 7);
  const calmWR    = wr(calm);
  const fearWR    = wr(highFear);
  const greedWR   = wr(highGreed);

  // ── Conviction vs outcome ─────────────────────────────────────────────────
  const hiConv = accountTrades.filter(t => (t.conviction_level || 0) >= 7);
  const loConv = accountTrades.filter(t => (t.conviction_level || 0) <= 4);
  const hiConvWR = wr(hiConv);
  const loConvWR = wr(loConv);

  // ── Setup performance ─────────────────────────────────────────────────────
  const setupMap = {};
  accountTrades.forEach(t => {
    const s = t.setup_type || "Unknown";
    if (!setupMap[s]) setupMap[s] = { wins: 0, losses: 0, total: 0, pnl: 0 };
    setupMap[s].total++;
    const k = classifyTrade(t);
    if (k === "win") setupMap[s].wins++;
    else if (k === "loss") setupMap[s].losses++;
    setupMap[s].pnl += tradeAmount(t, pnlMode);
  });
  const setupRows = Object.entries(setupMap)
    .filter(([, d]) => d.total >= 2)
    .map(([name, d]) => {
      const denom = d.wins + d.losses;
      return { name, wr: denom > 0 ? d.wins / denom : null, total: d.total, pnl: d.pnl };
    })
    .filter(r => r.wr !== null)
    .sort((a, b) => b.wr - a.wr);

  // ── Time-of-day ───────────────────────────────────────────────────────────
  const timeMap = {};
  accountTrades.forEach(t => {
    if (!t.entry_time) return;
    const h = parseInt(t.entry_time.split(":")[0], 10);
    const slot = h < 10 ? "Pre-10am" : h < 12 ? "10am–12pm" : h < 14 ? "12–2pm" : "2pm+";
    if (!timeMap[slot]) timeMap[slot] = { wins: 0, losses: 0, total: 0 };
    timeMap[slot].total++;
    const k = classifyTrade(t);
    if (k === "win") timeMap[slot].wins++;
    else if (k === "loss") timeMap[slot].losses++;
  });
  const timeRows = Object.entries(timeMap)
    .filter(([, d]) => d.total >= 2)
    .map(([slot, d]) => {
      const denom = d.wins + d.losses;
      return { slot, wr: denom > 0 ? d.wins / denom : null, total: d.total };
    })
    .filter(r => r.wr !== null)
    .sort((a, b) => b.wr - a.wr);

  // ── Streak ────────────────────────────────────────────────────────────────
  let streak = 0;
  let lastResult = null;
  for (const t of accountTrades) {
    const k = classifyTrade(t);
    if (k !== "win" && k !== "loss") continue;
    const label = k === "win" ? "Win" : "Loss";
    if (lastResult === null) {
      lastResult = label;
      streak = 1;
    } else if (label === lastResult) {
      streak++;
    } else {
      break;
    }
  }

  // ── Early exit rate ───────────────────────────────────────────────────────
  const earlyExitLosses = accountTrades.filter(t =>
    t.exit_reason === "manual_exit" && classifyTrade(t) === "loss"
  ).length;
  const earlyExitRate = accountTrades.length ? earlyExitLosses / accountTrades.length : 0;

  // ── Key insights ─────────────────────────────────────────────────────────
  const insights = [];
  if (calmWR != null && fearWR != null && calm.length >= 2 && highFear.length >= 2 && (calmWR - fearWR) > 0.15)
    insights.push({ type: "warning", text: `Win rate drops ${((calmWR - fearWR) * 100).toFixed(0)}pp when fear is high (${pct(fearWR)} vs ${pct(calmWR)} calm). Fear is costing you edge.` });
  if (calmWR != null && greedWR != null && calm.length >= 2 && highGreed.length >= 2 && (calmWR - greedWR) > 0.15)
    insights.push({ type: "warning", text: `Win rate drops ${((calmWR - greedWR) * 100).toFixed(0)}pp when greed is high (${pct(greedWR)} vs ${pct(calmWR)} calm). Greed is your edge-killer.` });
  if (hiConvWR != null && loConvWR != null && hiConv.length >= 2 && loConv.length >= 2) {
    const diff = hiConvWR - loConvWR;
    if (Math.abs(diff) > 0.15)
      insights.push({ type: diff > 0 ? "positive" : "warning", text: `High-conviction trades win ${pct(hiConvWR)} vs ${pct(loConvWR)} for low-conviction. ${diff > 0 ? "Trust your conviction." : "High conviction may be overconfidence — review those entries."}` });
  }
  if (earlyExitRate > 0.2)
    insights.push({ type: "warning", text: `${(earlyExitRate * 100).toFixed(0)}% of trades are early manual exits that closed as losses. Let your stops do their job.` });
  if (streak >= 3)
    insights.push({ type: lastResult === "Win" ? "positive" : "warning", text: lastResult === "Win" ? `You're on a ${streak}-trade win streak. Stay disciplined — don't oversize.` : `${streak}-trade losing streak. Reduce size or step back and identify the pattern first.` });
  if (disciplineTrend === "improving")
    insights.push({ type: "positive", text: `Discipline trending up across last ${last10.length} trades (${last10.join(" → ")}). The process improvements are showing.` });
  else if (disciplineTrend === "declining")
    insights.push({ type: "warning", text: `Discipline trending down across last ${last10.length} trades (${last10.join(" → ")}). Review your pre-trade routine.` });

  // ── Shared sub-styles ─────────────────────────────────────────────────────
  const tblHead = { fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "6px 10px", borderBottom: `1px solid ${C.border}` };
  const tblCell = { fontSize: 13, padding: "8px 10px", borderBottom: `1px solid ${C.border}20` };
  const wrColor = v => v >= 0.6 ? C.green : v >= 0.4 ? C.yellow : C.red;

  // ── Layout helpers ────────────────────────────────────────────────────────
  // Two-column grid on desktop, single column on mobile.
  const colGrid = isMobile
    ? { display: "flex", flexDirection: "column", gap: 16 }
    : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" };

  return (
    <div>
      {/* ── Not enough data warning ── */}
      {!hasEnough && (
        <Card glow={C.yellow} style={{ marginBottom: 20 }}>
          <p style={{ color: C.yellow, fontSize: 13 }}>⚠ Log at least 3 trades to unlock cross-trade analysis. You have {accountTrades.length} so far.</p>
        </Card>
      )}

      {/* ── KPI row — 5 stats across full width ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
        gap: 10,
        marginBottom: 20,
      }}>
        {[
          {
            label: "Avg Discipline",
            value: avgDiscipline != null ? `${avgDiscipline.toFixed(1)}/10` : "—",
            color: avgDiscipline >= 7 ? C.green : avgDiscipline >= 5 ? C.yellow : C.red,
          },
          {
            label: "Trend (last 10)",
            value: disciplineTrend
              ? disciplineTrend.charAt(0).toUpperCase() + disciplineTrend.slice(1)
              : "—",
            color: disciplineTrend === "improving" ? C.green : disciplineTrend === "declining" ? C.red : C.yellow,
          },
          {
            label: "Calm Win Rate",
            value: pct(calmWR),
            color: calmWR >= 0.6 ? C.green : calmWR >= 0.4 ? C.yellow : C.red,
          },
          {
            label: "Current Streak",
            value: streak >= 2 ? `${streak} ${lastResult}s` : "—",
            color: lastResult === "Win" ? C.green : lastResult === "Loss" ? C.red : C.muted,
          },
          {
            label: "Avg Conviction",
            value: avgFmt(accountTrades, "conviction_level"),
            color: C.text,
          },
        ].map(k => (
          <Card key={k.label} glow={k.color} style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* ── Key insights — full width ── */}
      {insights.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 10 }}>
            {insights.map((ins, i) => (
              <div key={i} style={{
                padding: "10px 14px", borderRadius: 8, fontSize: 13, lineHeight: 1.6,
                background: ins.type === "positive" ? C.green + "10" : C.red + "10",
                borderLeft: `3px solid ${ins.type === "positive" ? C.green : C.red}`,
                color: C.text,
              }}>
                {ins.type === "positive" ? "✓ " : "⚠ "}{ins.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Two-column body ── */}
      <div style={colGrid}>

        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Emotional State Win Rates */}
          <Card>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>😤 Emotional State Win Rates</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...tblHead, textAlign: "left" }}>State</th>
                  <th style={{ ...tblHead, textAlign: "center" }}>Trades</th>
                  <th style={{ ...tblHead, textAlign: "center" }}>Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Calm (fear ≤4, greed ≤4)", trades: calm.length,      wrVal: calmWR  },
                  { label: "High Fear (≥7)",            trades: highFear.length,  wrVal: fearWR  },
                  { label: "High Greed (≥7)",           trades: highGreed.length, wrVal: greedWR },
                ].map(row => (
                  <tr key={row.label}>
                    <td style={{ ...tblCell, color: C.text }}>{row.label}</td>
                    <td style={{ ...tblCell, textAlign: "center", color: C.muted }}>{row.trades}</td>
                    <td style={{ ...tblCell, textAlign: "center", color: row.wrVal != null ? wrColor(row.wrVal) : C.muted, fontWeight: 700 }}>
                      {row.trades >= 2 ? pct(row.wrVal) : <span style={{ color: C.muted }}>Need ≥2</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mental State Breakdown */}
          {stateRows.length > 0 && (
            <Card>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>🧠 Win Rate by Mental State</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...tblHead, textAlign: "left" }}>Mental State</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Trades</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stateRows.map(row => (
                    <tr key={row.state}>
                      <td style={{ ...tblCell, color: C.text }}>{row.state}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: C.muted }}>{row.total}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: wrColor(row.wr), fontWeight: 700 }}>{pct(row.wr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Conviction vs Outcome */}
          {(hiConv.length >= 2 || loConv.length >= 2) && (
            <Card>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>💡 Conviction vs Outcome</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...tblHead, textAlign: "left" }}>Conviction</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Trades</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "High (≥7)", trades: hiConv.length, wrVal: hiConvWR },
                    { label: "Low  (≤4)", trades: loConv.length, wrVal: loConvWR },
                  ].map(row => (
                    <tr key={row.label}>
                      <td style={{ ...tblCell, color: C.text }}>{row.label}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: C.muted }}>{row.trades}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: row.wrVal != null ? wrColor(row.wrVal) : C.muted, fontWeight: 700 }}>
                        {row.trades >= 2 ? pct(row.wrVal) : <span style={{ color: C.muted }}>Need ≥2</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Time of Day */}
          {timeRows.length > 0 && (
            <Card>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>🕐 Win Rate by Time of Day</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...tblHead, textAlign: "left" }}>Window</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Trades</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {timeRows.map(row => (
                    <tr key={row.slot}>
                      <td style={{ ...tblCell, color: C.text }}>{row.slot}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: C.muted }}>{row.total}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: wrColor(row.wr), fontWeight: 700 }}>{pct(row.wr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Setup Performance */}
          {setupRows.length > 0 && (
            <Card>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>📐 Setup Performance</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...tblHead, textAlign: "left" }}>Setup</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Trades</th>
                    <th style={{ ...tblHead, textAlign: "center" }}>Win Rate</th>
                    <th style={{ ...tblHead, textAlign: "right" }}>Net P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {setupRows.map(row => (
                    <tr key={row.name}>
                      <td style={{ ...tblCell, color: C.text }}>{row.name}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: C.muted }}>{row.total}</td>
                      <td style={{ ...tblCell, textAlign: "center", color: wrColor(row.wr), fontWeight: 700 }}>{pct(row.wr)}</td>
                      <td style={{ ...tblCell, textAlign: "right", color: row.pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>
                        {row.pnl >= 0 ? "+" : ""}${row.pnl.toFixed(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Discipline score distribution — mini sparkline via inline bars */}
          {last10.length >= 3 && (
            <Card>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
                📈 Discipline Score — Last {last10.length} Trades
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 48 }}>
                {[...last10].reverse().map((score, i) => {
                  const h = Math.max(4, (score / 10) * 48);
                  const col = score >= 7 ? C.green : score >= 5 ? C.yellow : C.red;
                  return (
                    <div
                      key={i}
                      title={`Trade ${i + 1}: ${score}/10`}
                      style={{
                        flex: 1,
                        height: h,
                        background: col,
                        borderRadius: 3,
                        opacity: 0.85,
                        transition: "height 0.2s",
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: C.muted }}>
                <span>Oldest</span>
                <span style={{ color: disciplineTrend === "improving" ? C.green : disciplineTrend === "declining" ? C.red : C.yellow, fontWeight: 700 }}>
                  {disciplineTrend ? disciplineTrend.charAt(0).toUpperCase() + disciplineTrend.slice(1) : "Stable"}
                </span>
                <span>Latest</span>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
